// Elevation analysis derived from a ping trail. Pure + runtime-neutral
// (no DOM, no fetch), so it can be imported by the SPA today and unit
// tested or reused server-side later. See ADR 0013.
//
// Altitude is the least reliable GPS signal — vertical error runs ~2-3x
// the horizontal. A naive sum of consecutive altitude deltas therefore
// reports huge phantom gain/loss from noise alone. We apply hysteresis:
// gain/loss only accrue once the run away from the last committed
// reference exceeds `noiseThresholdMeters`. This is the honest-precision
// stance applied to elevation — under-report rather than invent climb.

import type { Ping } from './wire';

export interface ElevationSample {
  t: number;
  altitude_meters: number;
  altitude_accuracy_meters: number | null;
}

export interface ElevationProfile {
  /** Altitude-bearing pings, chronological. Always length >= 1. */
  samples: ElevationSample[];
  /** Total ascent in metres, noise-filtered (>= 0). */
  gain: number;
  /** Total descent in metres, noise-filtered (>= 0). */
  loss: number;
  /** Lowest / highest sampled altitude. */
  min: number;
  max: number;
  /** Mean vertical accuracy over samples that report one; null if none do. */
  meanAccuracy: number | null;
}

export interface ElevationOptions {
  /** Hysteresis band; deltas smaller than this never accrue. Default 5 m. */
  noiseThresholdMeters?: number;
}

const DEFAULT_NOISE_THRESHOLD_M = 5;

/**
 * Build an elevation profile from a ping list. Pings without an altitude
 * are skipped. Returns `null` when no ping carries an altitude, so the
 * caller can hide its panel rather than render a flat/empty chart.
 */
export function buildElevationProfile(
  pings: readonly Ping[],
  opts: ElevationOptions = {},
): ElevationProfile | null {
  const threshold = opts.noiseThresholdMeters ?? DEFAULT_NOISE_THRESHOLD_M;

  const samples: ElevationSample[] = [];
  for (const p of pings) {
    if (typeof p.altitude_meters === 'number' && Number.isFinite(p.altitude_meters)) {
      samples.push({
        t: p.t,
        altitude_meters: p.altitude_meters,
        altitude_accuracy_meters: p.altitude_accuracy_meters,
      });
    }
  }
  if (samples.length === 0) return null;

  // Defend against out-of-order input; gain/loss is order-sensitive.
  samples.sort((a, b) => a.t - b.t);

  let min = samples[0].altitude_meters;
  let max = samples[0].altitude_meters;
  for (const s of samples) {
    if (s.altitude_meters < min) min = s.altitude_meters;
    if (s.altitude_meters > max) max = s.altitude_meters;
  }

  // Hysteresis accumulator: `ref` is the last committed altitude. We only
  // commit a gain/loss step once the current sample has moved more than
  // `threshold` away from `ref`, then advance `ref` to that sample.
  let gain = 0;
  let loss = 0;
  let ref = samples[0].altitude_meters;
  for (let i = 1; i < samples.length; i++) {
    const delta = samples[i].altitude_meters - ref;
    if (delta > threshold) {
      gain += delta;
      ref = samples[i].altitude_meters;
    } else if (delta < -threshold) {
      loss += -delta;
      ref = samples[i].altitude_meters;
    }
  }

  let accSum = 0;
  let accCount = 0;
  for (const s of samples) {
    if (typeof s.altitude_accuracy_meters === 'number' && Number.isFinite(s.altitude_accuracy_meters)) {
      accSum += s.altitude_accuracy_meters;
      accCount++;
    }
  }

  return {
    samples,
    gain,
    loss,
    min,
    max,
    meanAccuracy: accCount > 0 ? accSum / accCount : null,
  };
}
